import { queryOptions } from '@tanstack/react-query'
import { apiExchange, apiGet, apiPostJson } from '../api/client'
import { nextRuns, type Frequency } from './schedule'
import type { Definition, Locale, View, Words } from './types'

/**
 * RAPOARTELE SALVATE SI PROGRAMARILE LOR - in baza, prin
 * `SavedReportsController` (`/api/v2/reports/saved/...`).
 *
 * Pana la 9 octombrie 2026 se tineau in browser (localStorage, cheia
 * `od_reports_v1:<firma>:reports`). Ce a ramas acolo se aduce O DATA in baza
 * (`legacyToImport` + `importLegacy`), iar browserul tine minte ce a dus
 * (`od_reports_v1:<firma>:imported`) - nu se sterge nimic din el, deci nimic
 * nu se pierde daca importul pica. Serverul refuza oricum un al doilea import
 * al aceluiasi raport. Programarile din browser intra OPRITE: acolo nu
 * trimiteau nimic, deci nu pornesc singure sa trimita.
 *
 * Trimiterea e a cronului din ph4: "ruleaza acum" pune o rulare in coada, iar
 * istoricul arata rularile adevarate, cu fisierele lor.
 */

export type Visibility = 'private' | 'company'

export interface SavedBlock {
  title: Words
  definition: Definition
  wide: boolean
  view: View
}

export interface SavedReport {
  id: string
  name: string
  description: string
  company_id: number
  blocks: SavedBlock[]
  visibility: Visibility
  owner: string
  /** E al omului care se uita (altfel e unul partajat in firma). */
  mine: boolean
  created_at: string
  updated_at: string
}

export type ReportFormat = 'pdf' | 'xlsx' | 'csv'

export interface Schedule {
  id: string
  report_id: string
  enabled: boolean
  frequency: Frequency
  weekday: number
  month_day: number
  time: string
  timezone: string
  recipients: string[]
  locale: Locale
  formats: ReportFormat[]
  subject: string
  message: string
  /** Randurile din fiecare tabel puse in corpul emailului (0..50; lipsa = 10). Restul e in atasament. */
  body_rows?: number
  next_run_at: string | null
  last_run_at: string | null
}

export interface RunFile {
  id: number
  format: ReportFormat
  filename: string
  size: number
  expires_at: string | null
  available: boolean
}

export type RunStatus = 'queued' | 'running' | 'sent' | 'partial' | 'failed'

export interface Run {
  id: number
  trigger: 'schedule' | 'manual'
  status: RunStatus
  recipients: string[]
  formats: ReportFormat[]
  rows: number | null
  error: string | null
  queued_at: string | null
  started_at: string | null
  finished_at: string | null
  files: RunFile[]
}

export interface ReportsCan {
  view: boolean
  build: boolean
  schedule: boolean
  sensitive: boolean
}

export interface SavedOverview {
  company_id: number
  reports: SavedReport[]
  schedules: Schedule[]
  can: ReportsCan
}

export type ReportDraft = Pick<SavedReport, 'name' | 'description' | 'blocks' | 'visibility'> & { id?: string }
export type ScheduleDraft = Omit<Schedule, 'id' | 'next_run_at' | 'last_run_at'> & { id?: string }

export function nextRunAt(s: Omit<Schedule, 'id' | 'next_run_at' | 'last_run_at'>, now: Date): string | null {
  if (!s.enabled) return null
  return nextRuns(s, now, 1)[0]?.toISOString() ?? null
}

/* ---------------- API ---------------- */

export function savedReportsQuery(companyId: number) {
  return queryOptions({
    queryKey: ['reports', 'saved', companyId],
    queryFn: () => apiGet<SavedOverview>(`/reports/saved?company_id=${companyId}`),
    enabled: companyId > 0,
    staleTime: 30 * 1000,
  })
}

export function reportRunsQuery(reportId: string | null) {
  return queryOptions({
    queryKey: ['reports', 'runs', reportId],
    queryFn: () => apiGet<{ runs: Run[] }>(`/reports/saved/${reportId}/runs`).then((r) => r.runs),
    enabled: reportId !== null,
    /* O rulare in coada se termina la urmatoarea trecere a cronului: ecranul se uita din nou. */
    refetchInterval: (q) => ((q.state.data ?? []).some((r) => r.status === 'queued' || r.status === 'running') ? 15000 : false),
  })
}

/** Ce trimite dialogul de programare ca sa primeasca emailul desenat de server. */
export interface EmailPreviewRequest {
  company_id: number
  name: string
  blocks: { title: Words; definition: Definition }[]
  frequency: Frequency
  weekday: number
  month_day: number
  time: string
  timezone: string
  locale: Locale
  formats: ReportFormat[]
  subject: string
  message: string
  body_rows: number
}

export interface EmailPreview {
  subject: string
  html: string
  text: string
  /** Un bloc avea mai mult de 50 de randuri: previzualizarea e pe esantion. */
  sample: boolean
  period: string
  charts: number
}

/**
 * Emailul programarii, desenat de server cu `ReportEmail` (acelasi cu cronul
 * din ph4). Cheia e cererea intreaga: aceeasi forma = acelasi raspuns din cache.
 */
export function emailPreviewQuery(req: EmailPreviewRequest) {
  const { company_id, name, blocks, ...schedule } = req
  return queryOptions({
    queryKey: ['reports', 'email-preview', req],
    queryFn: () => apiPostJson<EmailPreview>('/reports/schedules/preview-email', { company_id, name, blocks, schedule }),
    staleTime: 60 * 1000,
    retry: false,
  })
}

export function saveReport(companyId: number, report: ReportDraft): Promise<SavedReport> {
  return apiPostJson<SavedReport>('/reports/saved', { company_id: companyId, report })
}

export function renameReport(id: string, name: string): Promise<SavedReport> {
  return apiPostJson<SavedReport>(`/reports/saved/${id}/rename`, { name })
}

export function duplicateReport(id: string, name: string): Promise<SavedReport> {
  return apiPostJson<SavedReport>(`/reports/saved/${id}/duplicate`, { name })
}

export function deleteReport(id: string): Promise<unknown> {
  return apiGet(`/reports/saved/${id}`, { method: 'DELETE' })
}

export function saveSchedule(draft: ScheduleDraft): Promise<Schedule> {
  return apiGet<Schedule>(`/reports/saved/${draft.report_id}/schedule`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(draft),
  })
}

export function deleteSchedule(reportId: string): Promise<unknown> {
  return apiGet(`/reports/saved/${reportId}/schedule`, { method: 'DELETE' })
}

export function runNow(reportId: string): Promise<{ id: number; status: RunStatus }> {
  return apiPostJson(`/reports/saved/${reportId}/run`, {})
}

export interface ImportResult {
  imported: number
  skipped: number
  schedules: number
  map: Record<string, number>
}

export function importLegacy(companyId: number, legacy: LegacyData): Promise<ImportResult> {
  return apiPostJson<ImportResult>('/reports/saved/import', { company_id: companyId, reports: legacy.reports, schedules: legacy.schedules })
}

/** Un fisier din istoric, salvat din browser. */
export async function downloadRunFile(file: RunFile): Promise<void> {
  const out = await apiExchange(`/reports/files/${file.id}`)
  const url = URL.createObjectURL(new Blob([out.bytes], { type: out.type }))
  const a = document.createElement('a')
  a.href = url
  a.download = out.filename || file.filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/* ---------------- Ce a ramas in browser ---------------- */

/** Doar ce trebuie din `Storage` - testele dau una care arunca. */
export type KeyValue = Pick<Storage, 'getItem' | 'setItem'>

export const LEGACY_PREFIX = 'od_reports_v1'

export interface LegacyData {
  reports: Record<string, unknown>[]
  schedules: Record<string, unknown>[]
}

const browserStorage = (): KeyValue | null => {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function readList(storage: KeyValue | null, key: string): Record<string, unknown>[] {
  try {
    const raw = storage?.getItem(key)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((x): x is Record<string, unknown> => typeof x === 'object' && x !== null) : []
  } catch {
    return []
  }
}

/** Rapoartele din browser, pe firma, care n-au fost inca aduse in baza. */
export function legacyToImport(companyId: number, storage: KeyValue | null = browserStorage()): LegacyData {
  const done = new Set(readList(storage, `${LEGACY_PREFIX}:${companyId}:imported`).map((x) => String(x.id)))
  const reports = readList(storage, `${LEGACY_PREFIX}:${companyId}:reports`).filter((r) => typeof r.id === 'string' && !done.has(r.id))
  const ids = new Set(reports.map((r) => String(r.id)))
  const schedules = readList(storage, `${LEGACY_PREFIX}:${companyId}:schedules`).filter((s) => ids.has(String(s.report_id)))
  return { reports, schedules }
}

/** Tine minte ce s-a dus in baza (cheia browserului -> id-ul din baza). */
export function markImported(companyId: number, map: Record<string, number>, storage: KeyValue | null = browserStorage()): void {
  const key = `${LEGACY_PREFIX}:${companyId}:imported`
  const list = readList(storage, key)
  const seen = new Set(list.map((x) => String(x.id)))
  for (const [id, to] of Object.entries(map)) if (!seen.has(id)) list.push({ id, to })
  try {
    storage?.setItem(key, JSON.stringify(list))
  } catch {
    /* fara stocare: serverul refuza oricum al doilea import */
  }
}
